import React, { memo, useCallback, useState, useRef } from 'react';
import { Button, Tag, message } from 'antd';
import { Tooltip } from '@mui/material';
import { StockOutlined, CopyOutlined, ReloadOutlined, LikeOutlined, DislikeOutlined, CheckOutlined } from '@ant-design/icons';
import logger from '../../../../utils/logger';
import { replaceMarketDataWithPlaceholder, formatTimestamp } from '../utils/chatUtils';
import { analytics, auth } from '../../../../firebaseConfig';
import { logEvent } from 'firebase/analytics';
import MarkdownRenderer from './MarkdownRenderer';
import { useTheme } from '../../../../context/ThemeContext';

/**
 * ChatMessage component for displaying individual messages
 */
const ChatMessage = memo(({
    message: msg,
    addLine,
    onRegenerate,
    chatId,
    instrument,
    category,
    index
}) => {
    const [copied, setCopied] = useState(false);
    const [feedback, setFeedback] = useState(null); // 'positive' | 'negative' | null
    const [showTimestamp, setShowTimestamp] = useState(false);
    const pointerStartRef = useRef({ x: null, y: null });

    // Get theme for syntax highlighting
    const { isDarkMode } = useTheme();

    // Copy message content to clipboard
    const handleCopy = useCallback(async () => {
        try {
            const textContent = msg.sender === 'assistant'
                ? msg.text.replace(/<[^>]*>/g, '') // Strip HTML for assistant
                : msg.text;
            await navigator.clipboard.writeText(textContent);
            setCopied(true);
            message.success('Copied to clipboard');
            setTimeout(() => setCopied(false), 2000);

            logEvent(analytics, 'message_copied', {
                sender: msg.sender,
                message_length: textContent.length
            });
        } catch (err) {
            message.error('Failed to copy');
            logger.error('Copy failed:', err);
        }
    }, [msg]);

    // Handle feedback (thumbs up/down)
    const handleFeedback = useCallback((type) => {
        setFeedback(type);
        const currentUser = auth?.currentUser;
        const modelContext = msg.model ||
            msg.metadata?.model ||
            (typeof msg.metadata === 'string' ? (() => {
                try {
                    const meta = JSON.parse(msg.metadata);
                    return meta?.model;
                } catch (err) {
                    return null;
                }
            })() : null);

        logEvent(analytics, 'message_feedback', {
            feedback_type: type,
            sender: msg.sender,
            chat_id: chatId || null,
            instrument: instrument || null,
            category: category || null,
            message_model: modelContext || 'unknown',
            message_timestamp: msg.timestamp || null,
            message_id: msg.id || null,
            message_index: typeof index === 'number' ? index : null,
            uid: currentUser?.uid || 'anonymous',
            user_email: currentUser?.email || null
        });
        message.success(type === 'positive' ? 'Thanks for the feedback!' : 'We\'ll try to improve');
    }, [category, chatId, index, instrument, msg]);

    // Render action bar for messages
    const renderActionBar = () => {
        if (msg.sender === 'user') {
            return null;
        }

        return (
            <div className="ntb-message-actions">
                <Tooltip title={copied ? "Copied!" : "Copy"} arrow placement="top">
                    <button
                        className={`ntb-message-action-btn ${copied ? 'copied' : ''}`}
                        onClick={handleCopy}
                        aria-label="Copy message"
                    >
                        {copied ? <CheckOutlined /> : <CopyOutlined />}
                    </button>
                </Tooltip>
                {onRegenerate && (
                    <Tooltip title="Regenerate response" arrow placement="top">
                        <button
                            className="ntb-message-action-btn"
                            onClick={onRegenerate}
                            aria-label="Regenerate response"
                        >
                            <ReloadOutlined />
                        </button>
                    </Tooltip>
                )}
                <Tooltip title="Good response" arrow placement="top">
                    <button
                        className={`ntb-message-action-btn feedback-positive ${feedback === 'positive' ? 'copied' : ''}`}
                        onClick={() => handleFeedback('positive')}
                        aria-label="Good response"
                        disabled={feedback !== null}
                    >
                        <LikeOutlined />
                    </button>
                </Tooltip>
                <Tooltip title="Poor response" arrow placement="top">
                    <button
                        className={`ntb-message-action-btn feedback-negative ${feedback === 'negative' ? 'copied' : ''}`}
                        onClick={() => handleFeedback('negative')}
                        aria-label="Poor response"
                        disabled={feedback !== null}
                    >
                        <DislikeOutlined />
                    </button>
                </Tooltip>
            </div>
        );
    };

    const renderSystemMessage = (parsedMsg, timestamp, type) => (
        <div className="ntb-chat-message system">
            <Tooltip title={type === 'market' ? 'Market data updated' : 'Summary created'} arrow>
                <div className="ntb-chat-message-content">
                    <StockOutlined style={{ fontSize: '16px', marginRight: '4px' }} />
                    <span>{type === 'market' ? 'Updated' : 'Summary created'}</span>
                    <span className="ntb-chat-message-timestamp" style={{ marginLeft: '4px', opacity: 0.7 }}>
                        {formatTimestamp(timestamp)}
                    </span>
                </div>
            </Tooltip>
        </div>
    );

    const handleAddLine = useCallback((line) => {
        if (addLine) {
            try {
                addLine({
                    price: line.price,
                    type: line.type,
                    title: line.description,
                });
            } catch (error) {
                logger.error('Error adding line:', error);
            }
        }
    }, [addLine]);

    const renderLines = useCallback((lines) => {
        if (!Array.isArray(lines)) {
            try {
                lines = JSON.parse(lines);
            } catch (e) {
                logger.error('Error parsing lines:', e);
                return null;
            }
        }

        return (
            <div className="ntb-chat-lines-container">
                <div className="ntb-chat-lines-header">
                    <span>Market Insights</span>
                    <Tooltip title="Add all lines to chart" arrow>
                        <span>
                            <Button 
                                type="text"
                                size="small"
                                className="ntb-chat-line-add-all-button"
                                icon={<StockOutlined />}
                                onClick={() => {
                                    if (Array.isArray(lines)) {
                                        lines.forEach(line => {
                                            handleAddLine(line);
                                        });
                                    }
                                }}
                            >
                                Add All
                            </Button>
                        </span>
                    </Tooltip>
                </div>
                {lines.map((line, index) => {
                    const formattedPrice = typeof line.price === 'number' 
                        ? new Intl.NumberFormat('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 8,
                            useGrouping: true
                        }).format(line.price)
                        : line.price;

                    const typeIcon = line.type === 'support' ? '↑' : 
                                   line.type === 'resistance' ? '↓' : 
                                   '•';

                    return (
                        <div key={index} className="ntb-chat-line-item">
                            <div className="ntb-chat-line-info">
                                <Tooltip title={`${line.type.charAt(0).toUpperCase() + line.type.slice(1)} level`} arrow>
                                    <span>
                                        <Tag color={
                                            line.type === 'support' ? 'green' : 
                                            line.type === 'resistance' ? 'red' : 
                                            'blue'
                                        }>
                                            {typeIcon} {line.type.charAt(0).toUpperCase() + line.type.slice(1)}
                                        </Tag>
                                    </span>
                                </Tooltip>
                                <Tooltip title="Price level" arrow>
                                    <span className="ntb-chat-line-price">{formattedPrice}</span>
                                </Tooltip>
                                <span className="ntb-chat-line-description">{line.description}</span>
                            </div>
                            <Tooltip title="Add to chart" arrow>
                                <span>
                                    <Button 
                                        type="text"
                                        size="small"
                                        className="ntb-chat-line-add-button"
                                        icon={<StockOutlined />}
                                        onClick={() => {
                                            handleAddLine(line);
                                        }}
                                    />
                                </span>
                            </Tooltip>
                        </div>
                    );
                })}
            </div>
        );
    }, [handleAddLine]);

    const resetGesture = useCallback(() => {
        pointerStartRef.current = { x: null, y: null };
        setShowTimestamp(false);
    }, []);

    const handleUserPointerDown = useCallback((event) => {
        if (msg.sender !== 'user') return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        pointerStartRef.current = {
            x: event.clientX,
            y: event.clientY
        };
    }, [msg.sender]);

    const handleUserPointerMove = useCallback((event) => {
        if (msg.sender !== 'user') return;
        const start = pointerStartRef.current;
        if (start.x === null) return;

        const deltaX = start.x - event.clientX;
        const deltaY = Math.abs(start.y - event.clientY);

        if (Math.abs(deltaX) > deltaY && deltaX > 20) {
            setShowTimestamp(true);
        } else if (deltaX < 10) {
            setShowTimestamp(false);
        }
    }, [msg.sender]);

    const handleUserPointerEnd = useCallback(() => {
        if (msg.sender !== 'user') return;
        resetGesture();
    }, [msg.sender, resetGesture]);

    if (msg.text && typeof msg.text === 'string') {
        try {
            const parsedMessage = JSON.parse(msg.text);
            if (parsedMessage.data === "Updated market data.") {
                return renderSystemMessage(parsedMessage, msg.timestamp, 'market');
            }
            if (parsedMessage.summary === "Summary created.") {
                return renderSystemMessage(parsedMessage, msg.timestamp, 'summary');
            }
            if (parsedMessage.lines !== undefined) {
                return renderLines(parsedMessage.lines, msg.timestamp, 'lines');
            }
        } catch (e) {
            // Not a JSON message, continue with normal rendering
        }
    }

    // Use MarkdownRenderer for assistant messages, plain text for user messages
    const formattedContent = msg.sender === "assistant"
        ? <MarkdownRenderer content={msg.text} isDarkMode={isDarkMode} />
        : replaceMarketDataWithPlaceholder(msg.text);

    const hasLines = msg.metadata?.lines &&
        (Array.isArray(msg.metadata.lines) ? msg.metadata.lines.length > 0 :
        JSON.parse(msg.metadata.lines)?.length > 0);

    // Render user message (bubble style)
    if (msg.sender === 'user') {
        return (
            <div
                className={`ntb-chat-message user ${showTimestamp ? 'timestamp-visible' : ''}`}
                onPointerDown={handleUserPointerDown}
                onPointerMove={handleUserPointerMove}
                onPointerUp={handleUserPointerEnd}
                onPointerLeave={handleUserPointerEnd}
            >
                <span className="ntb-user-timestamp">
                    {formatTimestamp(msg.timestamp)}
                </span>
                <div className="ntb-user-bubble">
                    <div className="ntb-message-body">
                        <div className="ntb-chat-message-content">
                            {formattedContent}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Render assistant message
    return (
        <div className={`ntb-chat-message assistant ${msg.isStreaming ? 'isStreaming' : ''} ${msg.isProcessing ? 'isProcessing' : ''}`}>
            <div className="ntb-message-body">
                <div className="ntb-chat-message-content">
                    {formattedContent}
                    {hasLines && renderLines(msg.metadata.lines)}
                </div>
                <div className="ntb-message-meta">
                    <div className="ntb-message-actions-wrapper">
                        {renderActionBar()}
                    </div>
                    <span className="ntb-chat-message-timestamp">
                        {formatTimestamp(msg.timestamp)}
                    </span>
                </div>
            </div>
        </div>
    );
});

export default ChatMessage; 
